// =======================================
// DATA TYPES
// =======================================

/**
 * Leave type options
 */
export type LeaveType = 'personal' | 'sick_leave' | 'public_holiday';

/**
 * TimeEntry data structure from submitted_timesheet_entries view
 */
export interface TimeEntry {
  entry_id: number;
  batch_id: number;
  project_id: number | null;
  project_name: string;
  job_number: string;
  employee_name: string;
  date: string;
  description: string | null;
  hours: number;
  leave_type: LeaveType | null;
  week_ending_date: string;
  entry_status: 'Submitted' | 'Allocated' | 'Hidden';
  project_lead: string;
  project_lead_name: string;
}

/**
 * Grouped timesheet entries data structure from submitted_timesheet_entries_grouped view
 */
export interface GroupedTimeEntry {
  batch_id: number;
  employee_id: string;
  week_ending_date: string;
  project_id: number | null;
  project_name: string;
  job_number: string;
  employee_name: string;
  project_lead: string;
  project_lead_name: string;
  entry_count: number;
  total_hours: number;
  earliest_date: string;
  latest_date: string;
  group_status: 'Submitted' | 'Approved' | 'Rejected';
  individual_statuses: Array<'Submitted' | 'Allocated' | 'Hidden'>;
  leave_type: LeaveType | null;
}

/**
 * Individual entry details for expanded grouped view
 */
export interface EntryDetail {
  entry_id: number;
  project_id: number | null;
  date: string;
  hours: number;
  description: string | null;
  status: 'Submitted' | 'Allocated' | 'Hidden';
  leave_type: LeaveType | null;
}

/**
 * Project data structure for reassignment
 */
export interface Project {
  id: number;
  name: string;
  job_number: string;
  project_lead: string;
  project_lead_name: string;
}

/**
 * Team member data structure for filtering
 */
export interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
  type: 'Engineer' | 'Draftsperson' | 'Accounts';
}

// =======================================
// COMPONENT CONFIGURATION
// =======================================

/**
 * Filter configuration for the timesheet approvals page
 */
export interface FilterConfig {
  projectId: number | null;
  projectLead: string | null;
  employeeId: string | null;
  entryStatus: string[];
  dateFrom: string;
  dateTo: string;
}

/**
 * Job selector configuration for reassignment
 */
export interface JobSelectorConfig {
  selectedEntry: TimeEntry | null;
  selectedProject: Project | null;
  adjustedHours: number;
}

/**
 * Grouped entry expansion state configuration
 */
export interface GroupedEntryState {
  expandedGroups: Set<string>; // Set of "batch_id:project_id" keys
  selectedEntryForEdit: EntryDetail | null;
  selectedGroup: GroupedTimeEntry | null;
}

/**
 * Entry detail edit configuration
 */
export interface EntryEditConfig {
  entry: EntryDetail;
  groupedEntry: GroupedTimeEntry;
  selectedProject: Project | null;
  adjustedHours: number;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

/**
 * Entry status options for filtering
 */
export const ENTRY_STATUS_OPTIONS = [
  'Submitted',
  'Allocated',
  'Hidden'
] as const;

export type EntryStatus = typeof ENTRY_STATUS_OPTIONS[number];

/**
 * Group status options for display and filtering
 */
export const GROUP_STATUS_OPTIONS = [
  { label: 'Submitted', value: 'Submitted' },
  { label: 'Allocated', value: 'Approved' },
  { label: 'Hidden', value: 'Rejected' }
] as const;

export type GroupStatus = typeof GROUP_STATUS_OPTIONS[number]['value'];

/**
 * Leave type labels for display
 */
export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  personal: 'Annual Leave',
  sick_leave: 'Sick Leave',
  public_holiday: 'Public Holiday'
};

/**
 * Date range preset options
 */
export const DATE_RANGE_PRESETS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
  { label: 'This week', value: 'thisWeek' },
  { label: 'This month', value: 'thisMonth' },
  { label: 'Custom range', value: 'custom' }
] as const;

// =======================================
// UTILITY FUNCTIONS
// =======================================

/**
 * Calculate date ranges for presets
 */
export function getDateRange(preset: string | number): { from: string; to: string } {
  const today = new Date();
  const to = today.toISOString().split('T')[0];
  
  switch (preset) {
    case 7:
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      return { from: weekAgo.toISOString().split('T')[0], to };
    
    case 30:
      const monthAgo = new Date(today);
      monthAgo.setDate(today.getDate() - 30);
      return { from: monthAgo.toISOString().split('T')[0], to };
    
    case 'thisWeek':
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return { from: startOfWeek.toISOString().split('T')[0], to };
    
    case 'thisMonth':
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: startOfMonth.toISOString().split('T')[0], to };
    
    default:
      return { from: to, to };
  }
}

/**
 * Generate unique key for grouped entry
 */
export function getGroupKey(batchId: number, projectId: number | null, leaveType?: LeaveType | null): string {
  if (leaveType && projectId === null) {
    return `${batchId}:leave_${leaveType}`;
  }
  return `${batchId}:${projectId}`;
}

/**
 * Get status color class for styling
 * Handles both individual entry statuses and group statuses
 */
export function getStatusClass(status: string): string {
  switch (status) {
    // Individual entry statuses
    case 'Allocated':
      return 'status-allocated';
    case 'Hidden':
      return 'status-hidden';
    case 'Submitted':
      return 'status-submitted';
    // Group statuses (map to same classes as individual statuses)
    case 'Approved':  // Groups with allocated entries
      return 'status-allocated';
    case 'Rejected':  // Groups with hidden entries
      return 'status-hidden';
    default:
      return 'status-default';
  }
}

/**
 * Format currency values
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(value);
}

/**
 * Format hours with 2 decimal places
 */
export function formatHours(hours: number): string {
  return hours.toFixed(2);
}

/**
 * Format date range for display
 */
export function formatDateRange(earliestDate: string, latestDate: string): string {
  const earliest = new Date(earliestDate);
  const latest = new Date(latestDate);
  
  if (earliest.getTime() === latest.getTime()) {
    return earliest.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  }
  
  return `${earliest.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })} - ${latest.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  })}`;
} 